const platform = require("platform");
import { MKUtils } from "./MKUtils";
import { MKConstants } from "../../constants";
import { MKLocalStorage } from "./MKLocalStorage";

/**
 * MKPlatform class.
 * 
 * Get details about current platform.
 * @internal
 */
export abstract class MKPlatform {
    // Flag to check if HW_SECURE_CRYPTO supported
    static wvDrmRobustnessLevel: string = "";

    /**
     * Get device profile.
     *
     * @returns device profile object as base64 encoded string will be returned.
     */
    public static async getDeviceProfile(): Promise<string> {
        // Check local storage for device profile and return if available from local storage
        const localStorage = MKLocalStorage.getInstance();
        const deviceProfile = localStorage.getDeviceProfile();
        
        if (deviceProfile) {
            const decodedDeviceProfile = MKUtils.base64Decode(deviceProfile);
            // Replace old device profile with base64 device profile as a fix for Bug 1616796.
            if (decodedDeviceProfile.includes("model") && decodedDeviceProfile.includes("deviceUUID")) {
                return deviceProfile;
            }
        }

        // construct a new device profile
        const currentDeviceProfile = {
            model: this.isChromeCast() ? "ChromeCast" : (this.isDesktopChromeBrowser() || this.isChromebook() || this.isFirefox() || this.isEdge() || this.isOpera() || this.isSafari() || this.isUbuntuChrome() || this.isUbuntuFirefox()) ? "Desktop" : (this.isTizenTV() || this.isWebOS() || this.isOperaTV()) ? "TV" : (this.isComcastX1() /*|| this.isAmazonKepler()*/) ? "STB" : (this.isXbox() || this.isPlayStation5() || this.isPlayStation4()) ? "CONSOLE" : (this.isAndroid() || this.isIOSDevice() || this.isIOSWebView()) ? "MOBILE" : "Unknown",
            osVersion: platform.os.version || platform.version,
            vendorName: this.getOSVendorName(),
            osName: this.getDrmForPlatform(),
            deviceUUID: this.getDeviceId(),
            wvLevel: this.getDrmForPlatform() === "HTML5" ? await this.getWidevineSecurityLevel() : undefined,
            supportsHevcDecode: await this.isHevc4KSupported()
        }

        // device profile changed - so clear the registered flag from local storage as
        // we will need to fire a new registration request for this change.
        localStorage.clearRegistered();

        // encode to base64
        const encodedDeviceProfile = MKUtils.base64Encode(JSON.stringify(currentDeviceProfile));

        // save the new device profile to local storage
        localStorage.setDeviceProfile(encodedDeviceProfile);

        // return device profile
        return encodedDeviceProfile;
    }

    /**
     * Get unique device identifier.
     *
     * @returns the unique device identifier.
     */
    private static getDeviceId(): string {
        // Check local storage for deviceId and return if available in local storage
        const localStorage = MKLocalStorage.getInstance();
        const deviceId = localStorage.getDeviceId();
        if (deviceId) return deviceId;

        // generate a new device Id
        const currentDeviceId = MKUtils.getUUID().replace("-", "");

        // device profile changed - so clear the registered flag from local storage as
        // we will need to fire a new registration request for this change.
        localStorage.clearRegistered();

        // update local storage
        localStorage.setDeviceId(currentDeviceId);

        // return device id
        return currentDeviceId;
    }

    /**
     * Get Widevine Security Level for the current platform.
     *
     * **Note:** It is advised to call this function only when getDrmForPlatform() === "HTML5".
     *
     * Security levels are:
     * - HW_SECURE_ALL - L1
     * - HW_SECURE_DECODE - L1
     * - HW_SECURE_CRYPTO - L2
     * - SW_SECURE_DECODE - L3
     * - SW_SECURE_CRYPTO - L3
     *
     * Below code derived from https://websites.fraunhofer.de/video-dev/enabling-hardware-drm-on-android-chrome-using-the-encrypted-media-extensions/
     *
     * @returns a promise that will always resolve with a security level string "L1" or "L3"
     */
    private static getWidevineSecurityLevel(): Promise<string> {
        return new Promise<string>((resolve, _) => {
            try {
                const keySystem = MKConstants.DrmKeySystem.Widevine;
                const hwSecureAllConfig = [{
                    'videoCapabilities': [{
                        'robustness': 'HW_SECURE_ALL', // L1
                        'contentType': 'video/mp4;codecs="avc1.42800C"'
                    }]
                }];
                const hwSecureCryptoConfig = [{
                    'videoCapabilities': [{
                        'robustness': 'HW_SECURE_CRYPTO', // L2
                        'contentType': 'video/mp4;codecs="avc1.42800C"'
                    }]
                }];
                navigator.requestMediaKeySystemAccess(keySystem, hwSecureAllConfig)
                    .then((keySystemAccess) => {
                        return keySystemAccess.createMediaKeys();
                    })
                    .then(() => {
                        // L1 is supported
                        MKPlatform.wvDrmRobustnessLevel = "HW_SECURE_ALL";
                        return resolve("L1");
                    })
                    .catch((_error) => {
                        navigator.requestMediaKeySystemAccess(keySystem, hwSecureCryptoConfig)
                            .then(() => {
                                // L2 is supported
                                MKPlatform.wvDrmRobustnessLevel = "HW_SECURE_CRYPTO";
                                return resolve("L3");
                            })
                            .catch(() => {
                                return resolve("L3");

                            });
                    });
            } catch {
                // navigator is not supported, then for Hisense/OperaTV or Tizen TV we return L1 and L3 otherwise
                if (this.isOperaTV() || this.isTizenTV()) {
                    return resolve("L1");
                }
                return resolve("L3");
            }
        });
    }

    /**
     * Get HEVC Decode Capability for the current platform.
     * @returns a promise that will resolves to true if HEVC decoding is supported, otherwise resolves to false. Defaults to false in any other case.
     */
    private static isHevc4KSupported(): Promise<boolean> {
        return new Promise<boolean>((resolve, _) => {
            try {
                let keySystem = MKConstants.DrmKeySystem.Widevine;
                switch (MKPlatform.getDrmForPlatform()) {
                    case MKConstants.DrmPlatform.Playready:
                        keySystem = MKConstants.DrmKeySystem.Playready;
                        break;
                    case MKConstants.DrmPlatform.Fairplay:
                        const is60fpsSupported = (window as any).WebKitMediaKeys.isTypeSupported(MKConstants.DrmKeySystem.Fairplay, 'video/mp4;codecs="hvc1.1.6.L153.B0"') ?? false;
                        const is30fpsSupported = (window as any).WebKitMediaKeys.isTypeSupported(MKConstants.DrmKeySystem.Fairplay, 'video/mp4;codecs="hvc1.1.6.L150.B0"') ?? false;
                        return resolve(is60fpsSupported || is30fpsSupported);
                    default:
                        break;
                }

                const config = (framerate: number, level: string) => ({
                    type: "media-source",
                    video: {
                        contentType: `video/mp4;codecs="hvc1.1.6.${level}.B0"`,
                        width: 3840,
                        height: 2160,
                        bitrate: 20000000,
                        framerate: framerate
                    },
                    keySystemConfiguration: {
                        keySystem: keySystem
                    }
                });

                Promise.all([
                    navigator.mediaCapabilities.decodingInfo(config(60, "L153") as any),
                    navigator.mediaCapabilities.decodingInfo(config(30, "L150") as any)
                ])
                .then(([e60fps, e30fps]) => {
                    return resolve(e60fps.supported || e30fps.supported);
                })
                .catch(() => {
                    return resolve(false);
                });
            } catch {
                return resolve(false);
            }
        });
    }

    /**
     * Get DRM group by platform.
     */
    private static getDrmForPlatform(): string {
        if (this.isIOSFamily()) {
            return MKConstants.DrmPlatform.Fairplay;
        } else if (this.isIE() || this.isEdge() && this.isWindows() || this.isXbox() || this.isPlayStation5() || this.isPlayStation4() || this.isWebOS() /*|| this.isAmazonKepler() */) {
            return MKConstants.DrmPlatform.Playready;
        } else {
            return MKConstants.DrmPlatform.Widevine;
        }
    }

    /**
     * Get Vendor name by platform.
     */
    private static getOSVendorName() {
        if (this.isWindows()) {
            return "Microsoft";
        } else if (this.isMac()) {
            return "Apple";
        } else if (this.isChromeCast()) {
            return "Google";
        } else if (this.isTizenTV()) {
            return "Samsung";
        } else if (this.isWebOS()) {
            return "LG";
        } else if (this.isOperaTV()) {
            return "Hisense";
        } else if (this.isComcastX1()) {
            return "COMCAST";
        } else if (this.isXbox()) {
            return "Xbox";
        } else if (this.isPlayStation5() || this.isPlayStation4()) {
            return "Playstation";
        } else if (this.isAndroid()) {
            return "Android";
        } else if (this.isIOSDevice()) {
            return "iOS";
        } else if (this.isChromebook()) {
            return "Chromebook";
        } else if (this.isLinux()) {
            return "Ubuntu";
        }/*else if (this.isAmazonKepler()) {
            return "AMAZON_KEPLER";
        } */ else {
            return "Unknown";
        }
    }

    private static isIE(): boolean {
        return platform.name === "IE";
    }

    private static isEdge(): boolean {
        return platform.name === "Microsoft Edge" && (this.isMac() || this.isWindows()) && !this.isXbox()
    }

    private static isMac(): boolean {
        return platform.os.family === "OS X";
    }

    private static isWindows(): boolean {
        return platform.os.family === "Windows" || platform.os.family === "Windows NT 10.0" || platform.os.family === "Windows Server 2008 R2 / 7";
    }

    private static isXbox(): boolean {
        return this.isWindows() && platform.ua && platform.ua.includes('Xbox');
    }

    public static isPlayStation5(): boolean {
        return platform.ua && platform.ua.includes("AppleWebKit") && platform.ua.includes("PlayStation 5");
    }

    public static isPlayStation4(): boolean {
        return platform.ua && platform.ua.includes("AppleWebKit") && platform.ua.includes("PlayStation") && platform.ua.includes("WebMAF");
    }

    public static isSafari(): boolean {
        return platform.name === "Safari" && this.isMac();
    }

    public static isIOSDevice(): boolean {
        return platform.ua && (platform.ua.includes("iPhone") || platform.ua.includes("iPad")) && platform.os.family === 'iOS';
    }

    private static isIOSWebView(): boolean {
        return (!platform.name || (!this.isEdge() && !this.isChrome() && !this.isFirefox() && !this.isOpera())) && (platform.ua && platform.ua.includes("AppleWebKit")) && (platform.os.family === "OS X");
    }

    private static isChrome(): boolean {
        return platform.name === "Chrome" && !this.isComcastX1() && !this.isOperaTV() && !this.isChromebook();
    }

    private static isFirefox(): boolean {
        return platform.name === "Firefox";
    }

    private static isOpera(): boolean {
        return platform.name === "Opera" && (this.isMac() || this.isWindows());
    }

    public static isComcastX1(): boolean {
        return platform.ua && platform.ua.includes("AppleWebKit") && (platform.ua.includes("WPE") || platform.ua.includes("XiOne") || platform.ua.includes("Sky"));
    }

    public static isOperaTV(): boolean {
        return platform.ua && ((platform.ua.includes("Hisense;SmartTV;") || platform.ua.includes("Onida;SmartTV;")) && platform.ua.includes("Model/Hisense") && platform.ua.includes("VIDAA"));
    }

    private static isChromebook(): boolean {
        return platform.ua && platform.ua.includes("CrOS") && !this.isDesktopChromeBrowser();
    }

    private static isDesktopChromeBrowser(): boolean {
        return (this.isMac() || this.isWindows()) && (this.isChrome() || this.isEdge()) && (!this.isXbox() || !this.isWebOS() || !this.isTizenTV());
    }

    public static isWebOS(): boolean {
        return this.isChrome() && this.isLinux() && platform.ua && platform.ua.includes("Web0S");
    }

    public static isTizenTV(): boolean {
        return platform.name === "Safari" && platform.os.family === "Tizen" && platform.ua && platform.ua.includes("SMART-TV; LINUX; Tizen");
    }

    private static isLinux(): boolean {
        return platform.os?.family?.includes("Linux") || platform.os?.family?.includes("Ubuntu");
    }

    public static isIOSFamily(): boolean {
        return (this.isSafari() || this.isIOSDevice() || this.isIOSWebView());
    }

    private static isChromeCast(): boolean {
        return this.isChrome() && (this.isLinux() || this.isAndroid()) && platform.ua && platform.ua.includes("CrKey");
    }

    public static isAndroid(): boolean {
        return platform.os.family && platform.os.family.includes("Android");
    }

    public static isTablet(): boolean {
        var isTablet = /android|ipad|tablet/i.test(navigator.userAgent);
        return isTablet;
    }

    public static isPhone(): boolean {
        const isPhone = this.isAndroid() || this.isIOSDevice();
        return isPhone;
    }

    public static isDesktop(): boolean {
        const isDesktop = (this.isMac() || this.isWindows()) && (!this.isXbox() || !this.isWebOS() || !this.isTizenTV() || !this.isComcastX1());
        return isDesktop;
    }

    /*  public static isAmazonKepler(): boolean {
        return platform.ua && platform.ua.includes("AppleWebKit") && platform.ua.includes("Mobile Chrome") && platform.ua.includes("Kepler");
    }*/

    public static isUbuntuFirefox(): boolean {
        return (this.isFirefox() && this.isLinux());
    }

    public static isUbuntuChrome(): boolean {
        return (this.isChrome() && this.isLinux());
    }

}